import { useTranslation } from 'react-i18next';
import type { FlatfinderEstate } from 'realitiq-utils/src/flatfinder';
import { Button } from '@/components/ui/button';
import { env } from '@/constants/env';
import {
  formatFlatfinderNumber,
  getFlatfinderAvailabilityColors,
  getFlatfinderLanguage,
} from './display';

export function DigitalTwinLinkButton({ className, token }: { className?: string; token: string }) {
  return (
    <Button asChild variant="outline" size="sm" className={`rounded-md border ${className ?? ''}`}>
      <a
        href={`${env.PUBLIC_EDITOR_URL}/view?token=${encodeURIComponent(token)}`}
        rel="noopener noreferrer"
        target="_blank"
      >
        3D
      </a>
    </Button>
  );
}

export function ViewerEstateListItem({
  estate,
  buildingName,
  hovered,
  onHover,
  onSelect,
}: {
  estate: FlatfinderEstate;
  buildingName?: string;
  hovered: boolean;
  onHover: (hovered: boolean) => void;
  onSelect: () => void;
}) {
  const language = getFlatfinderLanguage();
  const { t } = useTranslation();
  const statusColor = estate.availability
    ? getFlatfinderAvailabilityColors(estate.availability).fill
    : null;

  return (
    <fieldset
      aria-label={estate.name}
      className={`relative border-b border-slate-200 last:border-b-0 ${
        hovered ? 'bg-slate-50' : 'bg-white'
      }`}
      onMouseEnter={() => onHover(true)}
      onMouseLeave={() => onHover(false)}
    >
      <Button
        variant="ghost"
        className={`h-auto w-full flex-col items-stretch rounded-none border-0 px-4 py-4 text-left hover:bg-slate-50 lg:px-4 ${estate.digitalTwinLinkToken ? 'pr-20 lg:pr-20' : ''}`}
        type="button"
        onClick={onSelect}
        onFocus={() => onHover(true)}
        onBlur={() => onHover(false)}
      >
        <div className="flex min-w-0 items-center gap-2">
          {statusColor ? (
            <span className="flex size-5 shrink-0 items-center justify-center rounded-full bg-slate-100">
              <span className="size-2.5 rounded-full" style={{ backgroundColor: statusColor }} />
            </span>
          ) : null}
          <span className="min-w-0 truncate text-base font-semibold text-slate-800">
            {estate.name}
          </span>
          {estate.topNumber ? (
            <span className="ml-auto shrink-0 text-xs text-slate-500">{estate.topNumber}</span>
          ) : null}
        </div>
        <div className="mt-3 grid grid-cols-2 gap-x-3 gap-y-2 text-xs text-slate-700">
          {estate.rooms !== null ? (
            <span>
              {formatFlatfinderNumber(estate.rooms, language)} {t('rooms')}
            </span>
          ) : null}
          {estate.livingArea !== null ? (
            <span>{formatFlatfinderNumber(estate.livingArea, language)} m²</span>
          ) : null}
          {estate.floor !== null ? (
            <span>
              {t('floor')} {formatFlatfinderNumber(estate.floor, language)}
            </span>
          ) : null}
          {buildingName ? <span className="truncate">{buildingName}</span> : null}
        </div>
      </Button>
      {estate.digitalTwinLinkToken ? (
        <DigitalTwinLinkButton
          className="absolute right-4 top-4 h-8 min-w-11 px-2"
          token={estate.digitalTwinLinkToken}
        />
      ) : null}
    </fieldset>
  );
}
